/**
 * Quantity Discount Selector V3
 * - Cart-aware (mix & match): total cart qty drives active discount tier
 * - Quantity stepper replaces tier cards
 * - Projected pricing in CTA: total + savings + crossed-out base
 * - Shopify recommendations carousel ("These look great with")
 *
 * Cart monitoring (ported from v2):
 *   fetch intercept + native events + cart-bubble MutationObserver
 */

class QuantityDiscountCleanV3 extends HTMLElement {
  constructor() {
    super();
    this.basePrice = parseInt(this.dataset.basePrice) || 0;
    this.comparePrice = parseInt(this.dataset.comparePrice) || 0;
    this.cartQty = parseInt(this.dataset.cartQty) || 0;
    this.unitLabel = this.dataset.unitLabel || 'per stem';
    this.promoDiscount = parseInt(this.dataset.promoDiscount) || 0;

    this.tier2Qty = parseInt(this.dataset.tier2Qty) || 6;
    this.tier2Discount = parseInt(this.dataset.tier2Discount) || 5;
    this.tier3Qty = parseInt(this.dataset.tier3Qty) || 12;
    this.tier3Discount = parseInt(this.dataset.tier3Discount) || 10;

    this.productId = this.dataset.productId;
    this.selectedQuantity = 1;
    this.isUpdating = false;
  }

  connectedCallback() {
    this.init();
    this.setupCartListeners();
    this.refreshCartQuantity();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.removeCartListeners();
  }

  init() {
    this.stepper = this.querySelector('.qty-v3-stepper');
    this.qtyInput = this.querySelector('.qty-v3-input');
    this.hiddenQtyInput = this.querySelector('.qty-discount-input-v3');
    this.stepButtons = this.querySelectorAll('.qty-v3-step');

    this.addButton = this.querySelector('.qty-v3-atc');
    this.ctaText = this.querySelector('[data-cta-text]');
    this.savingsDisplay = this.querySelector('[data-savings-display]');
    this.newPriceDisplay = this.querySelector('[data-total-price]');

    this.recsRoot = this.querySelector('.qty-v3-recs');
    this.recsTrack = this.querySelector('[data-recs-track]');
    this.recsPrev = this.querySelector('.qty-v3-recs__nav--prev');
    this.recsNext = this.querySelector('.qty-v3-recs__nav--next');

    this.stepButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const delta = parseInt(btn.dataset.step) || 0;
        this.adjustQuantity(this.selectedQuantity + delta);
      });
    });

    this.qtyInput.addEventListener('input', () => {
      const v = parseInt(this.qtyInput.value);
      if (Number.isFinite(v) && v >= 1) this.adjustQuantity(v, { skipInputSync: true });
    });

    this.qtyInput.addEventListener('blur', () => {
      const v = parseInt(this.qtyInput.value);
      if (!Number.isFinite(v) || v < 1) this.adjustQuantity(1);
    });

    if (this.recsPrev) this.recsPrev.addEventListener('click', () => this.scrollRecs(-1));
    if (this.recsNext) this.recsNext.addEventListener('click', () => this.scrollRecs(1));

    this.adjustQuantity(1);
    this.updateMilestoneTracker();
  }

  /* ============================================================
     CART MONITORING (ported verbatim from v2)
     ============================================================ */

  setupCartListeners() {
    this.cartUpdateHandler = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:updated', this.cartUpdateHandler);
    document.addEventListener('cart:refresh', this.cartUpdateHandler);
    document.addEventListener('cart.requestComplete', this.cartUpdateHandler);
    document.addEventListener('shopify:section:load', this.cartUpdateHandler);
    document.addEventListener('shopify:section:rerender', this.cartUpdateHandler);
    this.setupCartDrawerObserver();
    this.interceptCartFetch();
  }

  removeCartListeners() {
    document.removeEventListener('cart:updated', this.cartUpdateHandler);
    document.removeEventListener('cart:refresh', this.cartUpdateHandler);
    document.removeEventListener('cart.requestComplete', this.cartUpdateHandler);
    document.removeEventListener('shopify:section:load', this.cartUpdateHandler);
    document.removeEventListener('shopify:section:rerender', this.cartUpdateHandler);
    if (this.cartObserver) this.cartObserver.disconnect();
    if (this.originalFetch) window.fetch = this.originalFetch;
  }

  setupCartDrawerObserver() {
    const cartCountBadge = document.querySelector(
      '.cart-count-bubble, [data-cart-count], .header__icon--cart .icon__badge, #cart-icon-bubble'
    );
    if (!cartCountBadge) return;
    this.cartObserver = new MutationObserver(() => this.handleCartUpdate({ type: 'mutation' }));
    this.cartObserver.observe(cartCountBadge, { childList: true, characterData: true, subtree: true });
  }

  interceptCartFetch() {
    if (!window.fetch || this.originalFetch) return;
    const originalFetch = window.fetch;
    this.originalFetch = originalFetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = args[0];
      if (
        typeof url === 'string' &&
        (url.includes('/cart/add') ||
          url.includes('/cart/update') ||
          url.includes('/cart/change') ||
          url.includes('/cart/clear'))
      ) {
        const clone = response.clone();
        clone.json().then(() => this.handleCartUpdate({ type: 'fetch-intercept' })).catch(() => {});
      }
      return response;
    };
  }

  async handleCartUpdate(event) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    await this.refreshCartQuantity();
    setTimeout(() => { this.isUpdating = false; }, 300);
  }

  async refreshCartQuantity() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const newCartQty = cart.items.reduce((total, item) => total + item.quantity, 0);
      if (newCartQty !== this.cartQty) {
        this.cartQty = newCartQty;
        this.dataset.cartQty = newCartQty;
        this.updateMilestoneTracker();
        this.updateButtonContent();
      }
    } catch (error) {
      console.error('[qty-v3] cart fetch failed:', error);
    }
  }

  /* ============================================================
     DISCOUNT LOGIC
     ============================================================ */

  getCurrentActiveDiscount() {
    if (this.cartQty >= this.tier3Qty) return this.tier3Discount;
    if (this.cartQty >= this.tier2Qty) return this.tier2Discount;
    return 0;
  }

  getDiscountAfterAdding(addQuantity) {
    const combined = this.cartQty + addQuantity;
    if (combined >= this.tier3Qty) return this.tier3Discount;
    if (combined >= this.tier2Qty) return this.tier2Discount;
    return 0;
  }

  /* ============================================================
     QUANTITY STEPPER
     ============================================================ */

  adjustQuantity(newQty, { skipInputSync = false } = {}) {
    const clamped = Math.max(1, parseInt(newQty) || 1);
    this.selectedQuantity = clamped;
    if (!skipInputSync) this.qtyInput.value = clamped;
    if (this.hiddenQtyInput) this.hiddenQtyInput.value = clamped;
    this.updateButtonContent();
    this.updateMilestoneTracker();
  }

  /* ============================================================
     MILESTONE TRACKER (ported from v2, scoped to v3 ids)
     ============================================================ */

  updateMilestoneTracker() {
    /* Everything in this tracker reports the cart as it actually stands. The
       stepper is deliberately NOT added in.

       It used to be: effectiveQty = cartQty + selectedQuantity. A lit node on
       the bar and the word UNLOCKED are both claims about the current cart, so
       with 5 in the cart and 1 sitting in the stepper the widget announced a 5%
       discount the customer had not earned — the cart showed no discount, and
       the counts disagreed by exactly the stepper value.

       Projection still happens where it is honest about being a projection:
       the ADD TO CART button, which says what this click will cost and save. */
    const cartQty = this.cartQty + this.selectedQuantity;

    const fill1 = this.querySelector('[data-progress-fill="1"]');
    const fill2 = this.querySelector('[data-progress-fill="2"]');
    const nodeMid = this.querySelector('[data-progress-node="mid"]');
    const nodeEnd = this.querySelector('[data-progress-node="end"]');
    const labelMid = this.querySelector('[data-progress-label="mid"]');
    const labelEnd = this.querySelector('[data-progress-label="end"]');

    if (fill1 && fill2 && nodeMid && nodeEnd) {
      const seg1Pct = Math.max(0, Math.min(100, (cartQty / this.tier2Qty) * 100));
      const seg2Range = Math.max(1, this.tier3Qty - this.tier2Qty);
      const seg2Pct = cartQty <= this.tier2Qty
        ? 0
        : Math.max(0, Math.min(100, ((cartQty - this.tier2Qty) / seg2Range) * 100));

      fill1.style.width = `${seg1Pct}%`;
      fill2.style.width = `${seg2Pct}%`;

      const midActive = cartQty >= this.tier2Qty;
      const endActive = cartQty >= this.tier3Qty;
      nodeMid.classList.toggle('is-active', midActive);
      nodeEnd.classList.toggle('is-active', endActive);
      labelMid && labelMid.classList.toggle('is-active', midActive);
      labelEnd && labelEnd.classList.toggle('is-active', endActive);

      this.classList.toggle('maxed-state', endActive);
    }

    /* Badge messaging — reports the cart, so "UNLOCKED" is only ever said once
       the discount genuinely applies at checkout. */
    const badgeContainer = this.querySelector('#activeDiscountBadgeV3');
    const badgeMainText = this.querySelector('#badgeMainTextV3');
    const badgeSubText = this.querySelector('#badgeSubTextV3');
    if (!badgeContainer || !badgeMainText || !badgeSubText) return;

    const arrowIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 7h6v6"></path><path d="m22 7-8.5 8.5-5-5L2 17"></path></svg>';

    if (cartQty >= this.tier3Qty) {
      badgeMainText.innerHTML = `🎉 ${this.tier3Discount}% DISCOUNT UNLOCKED!`;
      badgeSubText.innerHTML = `You've secured the maximum savings!`;
      badgeContainer.className = 'active-discount-badge tier-max-unlocked';
      badgeContainer.style.display = 'block';
    } else if (cartQty >= this.tier2Qty) {
      const needed = this.tier3Qty - cartQty;
      const itemWord = needed === 1 ? 'item' : 'items';
      badgeMainText.innerHTML = `🎉 ${this.tier2Discount}% DISCOUNT UNLOCKED!`;
      badgeSubText.innerHTML = `${arrowIcon} Add ${needed} more ${itemWord} to unlock ${this.tier3Discount}% off!`;
      badgeContainer.className = 'active-discount-badge tier-unlocked';
      badgeContainer.style.display = 'block';
    } else {
      /* "more" only reads right when something is already in the cart — on an
         empty one it is just "Add 6 items". */
      const needed = this.tier2Qty - cartQty;
      const itemWord = needed === 1 ? 'item' : 'items';
      const moreWord = cartQty > 0 ? 'more ' : '';
      badgeMainText.textContent = 'BUY MORE, SAVE MORE';
      badgeSubText.innerHTML = `${arrowIcon} Add ${needed} ${moreWord}${itemWord} to unlock ${this.tier2Discount}% off!`;
      badgeContainer.className = 'active-discount-badge tier-empty';
      badgeContainer.style.display = 'block';
    }
  }

  /* ============================================================
     ATC BUTTON CONTENT (projected pricing)
     ============================================================ */

  updateButtonContent() {
    if (!this.addButton) return;

    const qty = this.selectedQuantity;
    const actualDiscount = this.getDiscountAfterAdding(qty);

    const discountMultiplier = 1 - actualDiscount / 100;
    const discountedUnitPrice = this.basePrice * discountMultiplier;
    const totalPrice = discountedUnitPrice * qty;

    let finalDisplayPrice = totalPrice;
    let promoSavings = 0;
    if (this.promoDiscount > 0) {
      promoSavings = totalPrice * (this.promoDiscount / 100);
      finalDisplayPrice = totalPrice * (1 - this.promoDiscount / 100);
    }

    const hasProductSale = this.comparePrice > this.basePrice;

    let basePriceForSavings = null;
    let savingsAmount = 0;
    let savingsText = null;

    if (hasProductSale && actualDiscount === 0) {
      if (this.promoDiscount > 0) {
        basePriceForSavings = this.basePrice * qty;
        savingsAmount = basePriceForSavings - finalDisplayPrice;
        savingsText = `You save ${this.formatMoney(savingsAmount)} (${this.promoDiscount}% OFF BFCM)`;
      } else {
        basePriceForSavings = this.comparePrice * qty;
        savingsAmount = basePriceForSavings - totalPrice;
        const salePercent = Math.round((savingsAmount / basePriceForSavings) * 100);
        savingsText = `You save ${this.formatMoney(savingsAmount)} (${salePercent}% Off)`;
      }
    } else if (actualDiscount > 0) {
      basePriceForSavings = this.basePrice * qty;
      if (this.promoDiscount > 0) {
        savingsAmount = basePriceForSavings - finalDisplayPrice;
        savingsText = `You save ${this.formatMoney(savingsAmount)} (${actualDiscount}% OFF + ${this.promoDiscount}% OFF BFCM)`;
      } else {
        savingsAmount = basePriceForSavings - totalPrice;
        savingsText = `You save ${this.formatMoney(savingsAmount)} (${actualDiscount}% Off)`;
      }
    } else if (this.promoDiscount > 0) {
      basePriceForSavings = this.basePrice * qty;
      savingsAmount = basePriceForSavings - finalDisplayPrice;
      savingsText = `You save ${this.formatMoney(savingsAmount)} (${this.promoDiscount}% OFF BFCM)`;
    }

    if (this.ctaText) this.ctaText.textContent = `ADD ${qty} TO CART`;
    if (this.newPriceDisplay) this.newPriceDisplay.textContent = this.formatMoney(finalDisplayPrice);

    const showSavings = savingsAmount > 0 && savingsText;
    if (this.savingsDisplay) {
      this.savingsDisplay.textContent = showSavings ? savingsText : '';
    }
    if (this.addButton) {
      this.addButton.classList.toggle('qty-v3-atc--has-savings', !!showSavings);
    }

    const checkoutNotice = document.querySelector('.promo-checkout-notice');
    if (checkoutNotice && this.promoDiscount > 0) {
      checkoutNotice.textContent = `${this.promoDiscount}% BFCM DISCOUNT APPLIED AT CHECKOUT (-${this.formatMoney(promoSavings)})`;
    }
  }

  /* ============================================================
     RECOMMENDATIONS CAROUSEL ("These look great with")
     Uses Shopify's /recommendations/products.json endpoint
     ============================================================ */

  async loadRecommendations() {
    if (!this.recsTrack || !this.productId) return;
    const limit = parseInt((this.recsRoot && this.recsRoot.dataset && this.recsRoot.dataset.recsLimit) || 10, 10) || 10;
    const pinHandle = (this.recsRoot && this.recsRoot.dataset && this.recsRoot.dataset.recsPinHandle) || '';
    const url = `/recommendations/products.json?product_id=${encodeURIComponent(this.productId)}&limit=${limit}&intent=related`;
    try {
      const [resp, pinnedProduct] = await Promise.all([
        fetch(url),
        this.fetchPinnedProduct(pinHandle),
      ]);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const all = (data && data.products) || [];
      let products = all.filter((p) => p && !(p.tags || []).includes('hidden') && !(p.tags || []).includes('hidden-product'));

      // Pin a specific product to the front (always first), de-duplicated so
      // it never appears twice if Shopify's algorithm also surfaced it.
      if (pinnedProduct) {
        products = products.filter((p) => String(p.id) !== String(pinnedProduct.id));
        products.unshift(pinnedProduct);
        products = products.slice(0, limit);
      }

      if (products.length === 0) {
        this.recsRoot.style.display = 'none';
        return;
      }
      this.renderRecommendations(products);
    } catch (err) {
      console.error('[qty-v3] recommendations failed:', err);
      this.recsRoot.style.display = 'none';
    }
  }

  /* Fetch a single product (storefront JSON) to pin first in the carousel.
     /products/{handle}.js returns the same shape renderRecommendations reads.
     Returns null when no handle is set, the handle is the current product, or
     the request fails — so the carousel still renders its normal recs. */
  async fetchPinnedProduct(handle) {
    if (!handle) return null;
    try {
      const resp = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!resp.ok) return null;
      const product = await resp.json();
      if (!product || String(product.id) === String(this.productId)) return null;
      return product;
    } catch (_) {
      return null;
    }
  }

  renderRecommendations(products) {
    const cardsHtml = products
      .map((p) => {
        const url = p.url || `/products/${p.handle}`;
        const title = this.escapeHtml(p.title || '');
        const productType = this.escapeHtml(p.type || p.product_type || '');
        const featured = p.featured_image || (p.images && p.images[0]) || '';
        const imgSrc = featured ? this.scaleImage(featured, 400) : '';
        const priceCents = typeof p.price === 'number' ? p.price : parseInt(p.price) || 0;
        const priceFormatted = this.formatMoney(priceCents);
        const firstAvailable = Array.isArray(p.variants)
          ? p.variants.find((v) => v && v.available)
          : null;
        const variantId = firstAvailable
          ? firstAvailable.id
          : (p.variants && p.variants[0] && p.variants[0].id) || '';
        const canQuickAdd = !!firstAvailable;
        const quickAddBtn = canQuickAdd
          ? `<button type="button" class="qty-v3-recs__quick-add" data-variant-id="${variantId}" data-product-title="${title}" aria-label="Add ${title} to cart">
              <svg class="qty-v3-recs__quick-add-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
              <svg class="qty-v3-recs__quick-add-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
              <svg class="qty-v3-recs__quick-add-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </button>`
          : '';
        return `
          <a class="qty-v3-recs__card" href="${url}">
            <div class="qty-v3-recs__media">
              ${imgSrc ? `<img loading="lazy" src="${imgSrc}" alt="${title}">` : ''}
              ${quickAddBtn}
            </div>
            <div class="qty-v3-recs__meta">
              <span class="stamped-product-reviews-badge stamped-main-badge qty-v3-recs__stars"
                    data-id="${p.id}"
                    data-product-title="${title}"
                    data-product-type="${productType}"
                    style="display:block;"></span>
              <div class="qty-v3-recs__name">${title}</div>
              <div class="qty-v3-recs__price">${priceFormatted}</div>
            </div>
          </a>
        `;
      })
      .join('');
    this.recsTrack.innerHTML = cardsHtml;
    this.bindQuickAdd();
    this.loadStampedBadges();

    const updateNavVisibility = () => {
      if (!this.recsTrack) return;
      const overflow = this.recsTrack.scrollWidth > this.recsTrack.clientWidth + 1;
      if (this.recsPrev) this.recsPrev.hidden = !overflow;
      if (this.recsNext) this.recsNext.hidden = !overflow;
    };
    requestAnimationFrame(updateNavVisibility);
    window.addEventListener('resize', updateNavVisibility, { passive: true });
    this.recsTrack.addEventListener('scroll', () => this.updateRecsNavState(), { passive: true });
    this.updateRecsNavState();
  }

  bindQuickAdd() {
    const buttons = this.querySelectorAll('.qty-v3-recs__quick-add');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.quickAddToCart(btn);
      });
    });
  }

  async quickAddToCart(button) {
    if (!button || button.classList.contains('is-loading') || button.disabled) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    const cart = cartDrawer || cartNotification;

    button.classList.remove('is-success', 'is-error');
    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sectionIds = cart.getSectionsToRender().map((s) => s.id);
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(button);
        }
      }

      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      button.classList.remove('is-loading');
      button.classList.add('is-success');
      setTimeout(() => button.classList.remove('is-success'), 1400);

      if (cart && typeof cart.renderContents === 'function') {
        if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (err) {
      console.error('[qty-v3] quick add failed:', err);
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 1800);
    } finally {
      button.disabled = false;
    }
  }

  loadStampedBadges(attempt = 0) {
    if (typeof window.StampedFn !== 'undefined' && typeof window.StampedFn.loadBadges === 'function') {
      try { window.StampedFn.loadBadges(); } catch (_) {}
      return;
    }
    if (attempt >= 20) return; // ~10s max wait
    setTimeout(() => this.loadStampedBadges(attempt + 1), 500);
  }

  scrollRecs(direction) {
    if (!this.recsTrack) return;
    const card = this.recsTrack.querySelector('.qty-v3-recs__card');
    const step = card ? card.getBoundingClientRect().width + 12 : this.recsTrack.clientWidth * 0.8;
    this.recsTrack.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  updateRecsNavState() {
    if (!this.recsTrack || !this.recsPrev || !this.recsNext) return;
    const atStart = this.recsTrack.scrollLeft <= 1;
    const atEnd = this.recsTrack.scrollLeft + this.recsTrack.clientWidth >= this.recsTrack.scrollWidth - 1;
    this.recsPrev.classList.toggle('is-disabled', atStart);
    this.recsNext.classList.toggle('is-disabled', atEnd);
  }

  scaleImage(src, width) {
    if (!src) return '';
    if (src.includes('?')) return `${src}&width=${width}`;
    if (/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*)\./.test(src)) {
      return src.replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${width}x$1$2`);
    }
    return `${src}?width=${width}`;
  }

  /* ============================================================
     UTILITIES
     ============================================================ */

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('quantity-discount-clean-v3')) {
  customElements.define('quantity-discount-clean-v3', QuantityDiscountCleanV3);
}
